import type {
  LeaderboardEntry,
  LeaderboardSubmission,
  RankedLeaderboardEntry,
  RankedScore,
} from './types';

export const X_MICROS_SCALE = 1_000_000;
export const LEADERBOARD_LIMIT = 10;
export const LEADERBOARD_RETENTION_LIMIT = 30;

function assertPositiveSafeInteger(value: number, label: string): void {
  if (!Number.isSafeInteger(value) || value <= 0) {
    throw new RangeError(`${label} must be a positive safe integer.`);
  }
}

export function compareRankedScores(left: RankedScore, right: RankedScore): number {
  assertPositiveSafeInteger(left.profitCents, 'left.profitCents');
  assertPositiveSafeInteger(left.betCents, 'left.betCents');
  assertPositiveSafeInteger(right.profitCents, 'right.profitCents');
  assertPositiveSafeInteger(right.betCents, 'right.betCents');

  const leftProduct = BigInt(left.profitCents) * BigInt(right.betCents);
  const rightProduct = BigInt(right.profitCents) * BigInt(left.betCents);
  if (leftProduct !== rightProduct) return leftProduct > rightProduct ? -1 : 1;
  if (left.profitCents !== right.profitCents) return right.profitCents - left.profitCents;
  return left.betCents - right.betCents;
}

export function calculateXMicros(score: RankedScore): number {
  assertPositiveSafeInteger(score.profitCents, 'profitCents');
  assertPositiveSafeInteger(score.betCents, 'betCents');
  const scaled = (BigInt(score.profitCents) * BigInt(X_MICROS_SCALE)) / BigInt(score.betCents);
  const value = Number(scaled);
  if (!Number.isSafeInteger(value)) throw new RangeError('X value exceeds the supported range.');
  return value;
}

export function calculateXValue(score: RankedScore): number {
  return calculateXMicros(score) / X_MICROS_SCALE;
}

export function toLeaderboardSubmission(entry: LeaderboardEntry): LeaderboardSubmission {
  return Object.freeze({
    ...entry,
    xMicros: calculateXMicros(entry),
    xValue: calculateXValue(entry),
  });
}

export function sortLeaderboardEntries(
  entries: readonly LeaderboardEntry[],
  limit = LEADERBOARD_LIMIT,
): readonly RankedLeaderboardEntry[] {
  const unique = new Map<string, LeaderboardEntry>();
  for (const entry of entries) {
    const previous = unique.get(entry.clientEntryId);
    if (!previous || entry.createdAt > previous.createdAt) unique.set(entry.clientEntryId, entry);
  }

  return [...unique.values()]
    .sort((left, right) => (
      compareRankedScores(left, right)
      || right.createdAt - left.createdAt
      || left.clientEntryId.localeCompare(right.clientEntryId)
    ))
    .slice(0, Math.max(0, Math.trunc(limit)))
    .map((entry, index) => Object.freeze({ ...entry, rank: index + 1 }));
}

export function findRank(entries: readonly LeaderboardEntry[], clientEntryId: string): number | null {
  return sortLeaderboardEntries(entries, entries.length)
    .find((entry) => entry.clientEntryId === clientEntryId)?.rank ?? null;
}
